import { createSelector } from '@reduxjs/toolkit';

import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import {
  getAddedAttributeDomains,
  getIsAttributeDatatypeModified,
  getModifiedAttributeSpgs,
} from 'src/containers/ProjectDiagram/utils/draft/draftAttribute';
import {
  getIsDescriptionModified,
  getIsLabelModified,
} from 'src/containers/ProjectDiagram/utils/draft/draftModelConcept';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';

export const attributeDraftChangesSelector = (
  projectId: string,
  iri: string | undefined
) =>
  createSelector(diagramSnapshotSelector(projectId), (diagram) => {
    if (!diagram?.model?.modelData?.draftData || !iri) {
      return {
        addedDomains: new Set<string>(),
        isDatatypeModified: false,
        isDescriptionModified: false,
        isLabelModified: false,
        modifiedSensitivePropertyGroups: new Set<string>(),
      };
    }

    const currentAttribute: ModelConceptAttribute | undefined =
      diagram.model.modelData.attributesByIri[iri];
    const previousAttribute: ModelConceptAttribute | undefined =
      diagram.model.modelData.draftData.prevModelData.attributesByIri[iri];
    const isNewAttribute = !previousAttribute;

    const isDescriptionModified = getIsDescriptionModified({
      iri,
      isNewConcept: isNewAttribute,
      diagram,
    });

    const isLabelModified = getIsLabelModified({
      iri,
      isNewConcept: isNewAttribute,
      diagram,
    });

    const isDatatypeModified = getIsAttributeDatatypeModified({
      currentAttribute,
      previousAttribute,
      isNewAttribute,
    });

    const modifiedSensitivePropertyGroups = getModifiedAttributeSpgs({
      attributeIri: iri,
      isNewAttribute,
      diagram,
    });

    const addedDomains = getAddedAttributeDomains({
      currentAttribute,
      previousAttribute,
      isNewAttribute,
    });

    return {
      addedDomains,
      isDatatypeModified,
      isDescriptionModified,
      isLabelModified,
      modifiedSensitivePropertyGroups,
    };
  });
